import { useEffect, useState } from 'react';
import { TAB_MOUNTS } from '../projectBoardUtils';
import GeneralInfoTab from './GeneralInfoTab';
import SalesTab from './sales/SalesTab';
import CostingTab from './costing/CostingTab';
import PricingTab from './pricing/PricingTab';
import TimelineTab from './TimelineTab';
import ShippingTab from './shipping/ShippingTab';
import ServiceTab from './service/ServiceTab';
import FilesTab from './files/FilesTab';
import NotesTab from './notes/NotesTab';
import ReportLogoTab from './logo/ReportLogoTab';
import AhuComponentsTab from './ahu-summary/AhuComponentsTab';
import AhuAccessoriesTab from './ahu-summary/AhuAccessoriesTab';

/**
 * #tabs — the 14 static tab mounts (ContainerView → tmpl/container.tmpl),
 * inventory §2/§3.6/§5. Active tab = `.active` on the mount div (siblings
 * stripped); CSS hides non-active children. Mount-once keep-alive: a tab's
 * content mounts on FIRST activation and stays mounted (display toggled via
 * `.active`) — mirrors Backbone rendering each tab view once and toggling
 * (parity 18, 21).
 *
 * 3a/3c/3d/3e/3f scope: #project (General Info), #sales, #costing, #pricing,
 * #timeline, #shipping, #service, #files, #notes and #report_logo have
 * content. #accounting is never populated ([NEG], parity 24) and #ahus
 * is only reachable via a stale hash (the #ahus → #project rewrite happens at
 * load; parity 20). Files/Notes receive `active` — changePrjAppViewState
 * auto-clicks their FIRST sub-nav item on EVERY entry (parity 173/180).
 * NOTE: the Sales rail item is hidden by shared CSS on BOTH
 * sides (#menu-nav a[value=sales]{display:none} in project/styles.css) — the
 * Sales tab is deep-link-only (#sales), reproduced as-is.
 *
 * dev-sync F / KA-5258: #ahu_components and #ahu_accessories join the mount
 * list (container.tmpl:10-11). Both take `active` — their Backbone views
 * re-`fetch` on every re-open via the `open:{tab}` dispatcher event
 * (ahu_components.js:36-41), which the tabs mirror with a refetch-on-re-entry
 * hook rather than remounting.
 *
 * [NEG] honored: no #errorMsg / api.error.renderErrorMsg scaffolding
 * (parity 12); no dead #workflow_popup_button click binding (§3.6).
 */
const BoardTabs = ({
  projectId,
  activeTab,
}: {
  projectId: number;
  activeTab: string | null;
}) => {
  const [visited, setVisited] = useState<ReadonlySet<string>>(() => new Set());

  useEffect(() => {
    if (activeTab && !visited.has(activeTab)) {
      setVisited((prev) => new Set(prev).add(activeTab));
    }
  }, [activeTab, visited]);

  return (
    <div id="tabs">
      {TAB_MOUNTS.map(({ id, className }) => (
        <div
          key={id}
          id={id}
          className={`${className}${activeTab === id ? `${className ? ' ' : ''}active` : ''}`}
        >
          {id === 'project' && visited.has('project') && (
            <GeneralInfoTab projectId={projectId} />
          )}
          {id === 'sales' && visited.has('sales') && <SalesTab projectId={projectId} />}
          {id === 'costing' && visited.has('costing') && (
            <CostingTab projectId={projectId} />
          )}
          {id === 'pricing' && visited.has('pricing') && (
            <PricingTab projectId={projectId} />
          )}
          {id === 'timeline' && visited.has('timeline') && (
            <TimelineTab projectId={projectId} />
          )}
          {id === 'shipping' && visited.has('shipping') && (
            <ShippingTab projectId={projectId} />
          )}
          {id === 'service' && visited.has('service') && (
            <ServiceTab projectId={projectId} />
          )}
          {id === 'ahu_components' && visited.has('ahu_components') && (
            <AhuComponentsTab
              projectId={projectId}
              active={activeTab === 'ahu_components'}
            />
          )}
          {id === 'ahu_accessories' && visited.has('ahu_accessories') && (
            <AhuAccessoriesTab
              projectId={projectId}
              active={activeTab === 'ahu_accessories'}
            />
          )}
          {id === 'files' && visited.has('files') && (
            <FilesTab projectId={projectId} active={activeTab === 'files'} />
          )}
          {id === 'notes' && visited.has('notes') && (
            <NotesTab projectId={projectId} active={activeTab === 'notes'} />
          )}
          {id === 'report_logo' && visited.has('report_logo') && (
            <ReportLogoTab projectId={projectId} />
          )}
        </div>
      ))}
    </div>
  );
};

export default BoardTabs;
